// ── AI Store ────────────────────────────────────────────────────────────────
// Zustand store for the in-app AI chat system. Manages backend state,
// chat history, and loading indicators. Entirely separate from the MCP path.

import { create } from 'zustand';
import type { AIBackend, AIBackendStatus, AIBackendType, AIChatMessage, AIModelOption, AIOperationResult } from '../services/ai/types';
import { selectBestBackend, createBackend, probeAllBackends, type BackendProbeResult } from '../services/ai/backends/BackendSelector';
import { buildSystemPrompt } from '../services/ai/systemPrompt';
import { buildAIContext, formatContextForPrompt } from '../services/ai/contextBuilder';
import { parseAIResponse, executeOperations } from '../services/ai/AIExecutor';
import { AI_RESPONSE_SCHEMA } from '../services/ai/responseSchema';
import { useDiagramStore } from './diagramStore';
import { snapshotFromStore } from './undoStore';

const BACKEND_STORAGE_KEY = 'velostruct-ai-backend';

let _idCounter = 0;
function chatId(): string {
  return `ai-msg-${Date.now()}-${++_idCounter}`;
}

interface AIStore {
  // Panel
  isPanelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  togglePanel: () => void;

  // Backend
  backend: AIBackend | null;
  backendType: AIBackendType;
  backendStatus: AIBackendStatus;
  downloadProgress: number;
  availableBackends: BackendProbeResult[];
  availableModels: AIModelOption[];
  selectedModel: string | null;

  // Chat
  messages: AIChatMessage[];
  isProcessing: boolean;

  // Actions
  probeBackends: () => Promise<void>;
  selectBackend: (type?: AIBackendType) => Promise<void>;
  setSelectedModel: (id: string) => Promise<void>;
  initializeBackend: () => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
  clearChat: () => void;
}

async function loadModels(backend: AIBackend | null): Promise<{ availableModels: AIModelOption[]; selectedModel: string | null }> {
  if (!backend?.listModels) return { availableModels: [], selectedModel: backend?.getSelectedModel?.() ?? null };
  try {
    const availableModels = await backend.listModels();
    return { availableModels, selectedModel: backend.getSelectedModel?.() ?? null };
  } catch {
    return { availableModels: [], selectedModel: backend.getSelectedModel?.() ?? null };
  }
}

export const useAIStore = create<AIStore>((set, get) => ({
  // ── Panel ──
  isPanelOpen: false,
  setPanelOpen: (open) => set({ isPanelOpen: open }),
  togglePanel: () => set(s => ({ isPanelOpen: !s.isPanelOpen })),

  // ── Backend ──
  backend: null,
  backendType: 'none',
  backendStatus: 'unavailable',
  downloadProgress: 0,
  availableBackends: [],
  availableModels: [],
  selectedModel: null,

  // ── Chat ──
  messages: [],
  isProcessing: false,

  // ── Actions ──
  probeBackends: async () => {
    const results = await probeAllBackends();
    set({ availableBackends: results });
  },

  selectBackend: async (type) => {
    const current = get().backend;
    if (current) current.destroy();

    let backend: AIBackend | null = null;
    if (type) {
      backend = createBackend(type);
    } else {
      const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(BACKEND_STORAGE_KEY) as AIBackendType | null : null;
      if (saved && saved !== 'none') {
        try { backend = createBackend(saved); } catch { backend = null; }
      }
      if (!backend) backend = await selectBestBackend();
    }

    if (!backend) {
      set({ backend: null, backendType: 'none', backendStatus: 'unavailable', availableModels: [], selectedModel: null });
      return;
    }

    const status = await backend.checkAvailability();
    const models = await loadModels(backend);
    try { localStorage.setItem(BACKEND_STORAGE_KEY, backend.type); } catch { /* ignore */ }
    set({ backend, backendType: backend.type, backendStatus: status, ...models });
  },

  setSelectedModel: async (id) => {
    const { backend } = get();
    if (!backend?.setSelectedModel) return;
    backend.setSelectedModel(id);
    const status = await backend.checkAvailability();
    set({ selectedModel: id, backendStatus: status, downloadProgress: 0 });
  },

  initializeBackend: async () => {
    const { backend } = get();
    if (!backend) throw new Error('No backend selected');

    set({ backendStatus: 'downloading', downloadProgress: 0 });
    try {
      await backend.initialize((pct) => set({ downloadProgress: pct }));
      const models = await loadModels(backend);
      set({ backendStatus: 'ready', downloadProgress: 1, ...models });
    } catch (err) {
      set({ backendStatus: 'error' });
      throw err;
    }
  },

  sendMessage: async (text: string) => {
    const { backend, backendStatus, messages: prevMessages } = get();
    if (!backend) return;
    if (backendStatus !== 'ready') {
      const notice: AIChatMessage = {
        id: chatId(),
        role: 'assistant',
        content: 'Load a model in AI settings before chatting. Downloads no longer start automatically when you send a message.',
        timestamp: Date.now(),
      };
      set({ messages: [...prevMessages, notice] });
      return;
    }

    const userMsg: AIChatMessage = {
      id: chatId(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
    };
    set({ messages: [...prevMessages, userMsg], isProcessing: true });

    try {
      const diagramState = useDiagramStore.getState();
      const ctx = buildAIContext(text);
      const contextStr = formatContextForPrompt(ctx);
      const systemPrompt = buildSystemPrompt(diagramState.diagramType);

      const history = prevMessages
        .filter((m): m is AIChatMessage & { role: 'user' | 'assistant' } => m.role === 'user' || m.role === 'assistant')
        .slice(-8)
        .map(m => ({ role: m.role, content: m.content }));
      const fullMessage = `${contextStr}\n\nUSER REQUEST: ${text}`;

      const rawResponse = await backend.prompt(systemPrompt, fullMessage, AI_RESPONSE_SCHEMA, history);
      const aiResponse = parseAIResponse(rawResponse);

      let operationResults: AIOperationResult[] = [];
      if (aiResponse.operations.length > 0) {
        snapshotFromStore();
        operationResults = await executeOperations(aiResponse.operations);
        const failed = operationResults.filter(r => !r.success);
        if (failed.length > 0) {
          aiResponse.reply += `\n\n${failed.length} operation(s) failed: ${failed.map(f => `${f.method}: ${f.error}`).join('; ')}`;
        }
      }

      const assistantMsg: AIChatMessage = {
        id: chatId(),
        role: 'assistant',
        content: aiResponse.reply,
        timestamp: Date.now(),
        operations: operationResults,
      };

      set(s => ({
        messages: [...s.messages, assistantMsg],
        isProcessing: false,
      }));
    } catch (err) {
      const errorMsg: AIChatMessage = {
        id: chatId(),
        role: 'assistant',
        content: `Error: ${err instanceof Error ? err.message : String(err)}`,
        timestamp: Date.now(),
      };
      set(s => ({
        messages: [...s.messages, errorMsg],
        isProcessing: false,
      }));
    }
  },

  clearChat: () => {
    get().backend?.resetConversation?.();
    set({ messages: [] });
  },
}));
